import { LearningOpportunityFilterState } from "@/utils/browseFilter";
import { BrowseQueryParams } from "../pages/browse";
import { mcn } from "../utils";
import { IconActionFilter } from "./Icons";
import { LearningOpportunityStatus, LearningOpportunityType, StudyFormat } from "@/server/backend-api/generated";
import { Assert } from "@/utils/assert";

export function parseFilterStateFromQueryParams(query: BrowseQueryParams) {
  const filter: LearningOpportunityFilterState = {
    selectedLearningOppTypes: [],
    selectedTopicIds: [],
    selectedLearningOppStatuses: [],
    selectedUniversityIds: [],
    selectedStudyFormats: [],
    selectedTagIds: [],
    selectedLevelIds: [],
    selectedPaces: [],
    selectedCredits: [],
    selectedStudyPeriodsFrom: [],
    selectedStudyPeriodsTo: [],
    selectedAudienceIds: [],
  };

  const asArray = (value: string | string[] | undefined): string[] | null => {
    if (value === undefined) {
      return null;
    }
    return typeof value === "string" ? [value] : value;
  };

  const asEnumArray = <T extends number>(
    value: string | string[] | undefined
  ): T[] | null =>
    asArray(value)
      ?.map((x) => parseInt(x))
      .map((x) => x as T)
      .filter((x) => x !== undefined) ?? null;

  const learningOppTypes = asEnumArray<LearningOpportunityType>(
    query.learningOppTypes
  );
  if (learningOppTypes !== null) {
    for (const learningOppType of learningOppTypes) {
      filter.selectedLearningOppTypes.push(learningOppType);
    }
  }

  const topicIds = asArray(query.topicIds);
  if (topicIds !== null) {
    for (const topicId of topicIds) {
      Assert.check(typeof topicId === "string");
      filter.selectedTopicIds.push(topicId);
    }
  }

  const levelIds = asArray(query.levelIds);
  if (levelIds !== null) {
    for (const levelId of levelIds) {
      Assert.check(typeof levelId === "string");
      filter.selectedLevelIds.push(levelId);
    }
  }
  const audienceIds = asArray(query.audienceIds);
  if (audienceIds !== null) {
    for (const audienceId of audienceIds) {
      Assert.check(typeof audienceId === "string");
      filter.selectedAudienceIds.push(audienceId);
    }
  }

  const tagIds = asArray(query.tagIds);
  if (tagIds !== null) {
    for (const tagId of tagIds) {
      Assert.check(typeof tagId === "string");
      filter.selectedTagIds.push(tagId);
    }
  }

  const universityIds = asArray(query.universityIds);
  if (universityIds !== null) {
    for (const universityId of universityIds) {
      Assert.check(typeof universityId === "string");
      filter.selectedUniversityIds.push(universityId);
    }
  }

  const learningOppStatuses = asEnumArray<LearningOpportunityStatus>(
    query.learningOppStatuses
  );
  if (learningOppStatuses !== null) {
    for (const learningOppStatus of learningOppStatuses) {
      filter.selectedLearningOppStatuses.push(learningOppStatus);
    }
  }

  const studyFormats = asEnumArray<StudyFormat>(query.studyFormats);
  if (studyFormats !== null) {
    for (const studyFormat of studyFormats) {
      filter.selectedStudyFormats.push(studyFormat);
    }
  }

  const paces = asArray(query.paces);
  if (paces !== null) {
    for (const pace of paces) {
      Assert.check(typeof pace === "string");
      filter.selectedPaces.push(pace);
    }
  }
  const credits = asArray(query.credits);
  if (credits !== null) {
    for (const credit of credits) {
      Assert.check(typeof credit === "string");
      filter.selectedCredits.push(credit);
    }
  }

  const studyPeriodsFrom = asArray(query.studyPeriodsFrom);
  if (studyPeriodsFrom !== null) {
    for (const studyPeriod of studyPeriodsFrom) {
      Assert.check(typeof studyPeriod === "string");
      filter.selectedStudyPeriodsFrom.push(studyPeriod);
    }
  }
  const studyPeriodsTo = asArray(query.studyPeriodsTo);
  if (studyPeriodsTo !== null) {
    for (const studyPeriod of studyPeriodsTo) {
      Assert.check(typeof studyPeriod === "string");
      filter.selectedStudyPeriodsTo.push(studyPeriod);
    }
  }
  return filter;
}

export function browseQueryParamsFromFilter(
  filter: LearningOpportunityFilterState
): BrowseQueryParams {
  return {
    learningOppTypes: filter.selectedLearningOppTypes.map((x) => x.toString()),
    topicIds: filter.selectedTopicIds,
    universityIds: filter.selectedUniversityIds,
    learningOppStatuses: filter.selectedLearningOppStatuses.map((x) =>
      x.toString()
    ),
    studyFormats: filter.selectedStudyFormats.map((x) => x.toString()),
    tagIds: filter.selectedTagIds,
    levelIds: filter.selectedLevelIds.map((x) => x.toString()),
    paces: filter.selectedPaces,
    credits: filter.selectedCredits,
    studyPeriodsFrom: filter.selectedStudyPeriodsFrom,
    studyPeriodsTo: filter.selectedStudyPeriodsTo,
    audienceIds: filter.selectedAudienceIds,
  };
}

export const LOCountHeader = ({
  learningOppsCount,
}: {
  learningOppsCount: number;
}) => {
  return (
    <h2 className="text-style-headline5">
      {learningOppsCount} learning opportunities
    </h2>
  );
};

export const MobileFilterButtonRow = (props: {
  className: string;
  learningOppsCount: number;
  selectedFilterCount: number;
  onFilterButtonClick: () => void;
}) => {
  return (
    <div
      className={mcn(
        "flex justify-between items-center",
        "tablet-lg:hidden",
        props.className
      )}
    >
      <LOCountHeader learningOppsCount={props.learningOppsCount} />
      <button
        className="p-3 rounded-3 flex justify-center bg-light-blue"
        onClick={props.onFilterButtonClick}
      >
        {props.selectedFilterCount > 0 ? (
          <span className="w-6 h-6 mr-xs text-blue bg-yellow-90 rounded-full">
            {props.selectedFilterCount}
          </span>
        ) : (
          <IconActionFilter className="w-6 h-6 mr-xs" />
        )}
        Filters
      </button>
    </div>
  );
};